import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next' 

import Pagination from 'components/Pagination';
import AcceptanceProduction from './modals/AcceptanceProduction';
import ShipmentDistributor from './modals/ShipmentDistributor';

import { GET, httpOk, POST } from 'helpers/api';
import { formatDate, formatMoney } from 'helpers/helpers';
import { Link } from 'react-router-dom';

import { ReactComponent as OrdersIcon } from 'assets/icons/sidebar/orders.svg';
import { ReactComponent as ReportUpIcon } from 'assets/icons/report_up.svg';
import { ReactComponent as WarningCircleIcon } from 'assets/icons/warning_circle.svg';
import { ReactComponent as TelegramIcon } from 'assets/icons/telegram.svg';
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg';

function Dashboard() {
    const { t } = useTranslation() 

    const initFilterData = {
        page: 1,
        page_size: 20,
    };

    const [acceptanceProductionModal, setAcceptanceProductionModal] = useState(false)
    const [shipmentDistributorModal, setShipmentDistributorModal] = useState(false)

    const [filterData, setFilterData] = useState(initFilterData)

    const [data, setData] = useState({
        in_progress_orders: [],
        planned_orders: [],
        counters: {},
    })

    async function processOrder(item) {
        const response = await POST(`/sales/orders/${item.id}/process/`)
        if (httpOk(response)) {
            getData();
        }
    }

    const handlePaginate = (args) => {
        setFilterData(prev => ({
            ...prev,
            page: args.page + 1,
            page_size: args.page_size
        }));
    };

    const getData = useCallback(async () => {
        const response = await GET('/sales/dashboard/', filterData);

        if (httpOk(response)) {
            setData({ ...response.data });
        }
    }, [filterData]); 

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <>
            <div>
                <div className="main-content-title">
                    {t('sales_dashboard')}
                </div>

                <div className="main-content-subtitle">
                    {t('order_management_distributors_desc')}
                </div>
            </div>

            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('new_orders')}
                            </div>
                            <div className="value primary">
                                {formatMoney(data?.counters?.new_count)}
                            </div>
                        </div>

                        <div className="icon primary">
                            <OrdersIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('in_processing')}
                            </div>
                            <div className="value text-warning">
                                {formatMoney(data?.counters?.processing_count)}
                            </div>
                        </div>

                        <div className="icon warning">
                            <WarningCircleIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('from_telegram')}
                            </div>
                            <div className="value text-purple">
                                {formatMoney(data?.counters?.telegram_count)}
                            </div>
                        </div>

                        <div className="icon purple">
                            <TelegramIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('total_amount')}
                            </div>
                            <div className="value text-success">
                                {formatMoney(data?.counters?.total_amount)}
                            </div>
                        </div>

                        <div className="icon success">
                            <ReportUpIcon />
                        </div>
                    </div>
                </div>
            </div>

            {data?.pending_orders?.length > 0 &&
                <div className="alert second alert-primary">
                    <div className="alert-title">
                        <OrdersIcon />
                        {t('low_free_stock_alert')}
                    </div>

                    <div className="mb-16">
                        {t('low_stock_warning_desc')}
                    </div>

                    <div className="d-flex flex-column gap-8">
                        {data.pending_orders.map((item, index) => (
                            <div className="card second" key={index}>
                                <div className="d-between mb-10">
                                    <div>
                                        <div className="text-first">
                                            {item.order_number}
                                        </div>
                                        <div className="text-second">
                                            {item.distributor_name}
                                        </div>
                                    </div>

                                    <div className="d-flex align-items=center gap-8">
                                        <div className={`source source-${item.source}`}>
                                            {item.source}
                                        </div>

                                        <div className="text-first fz16">
                                            {formatMoney(item.total_amount)} {t('uzs')}
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex gap-8">
                                    <button className="dark small" onClick={() => processOrder(item)}>
                                        {t('process')}
                                    </button>
                                    <Link to="/sales/warehouse-fg">
                                        <button className="white small">
                                            {t('check_stock')}
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        ))
                        }
                    </div>
                </div>
            }

            <div className="card">
                <div className="d-between mb-30">
                    <div className="fz16 text-third">
                        {t('orders_to_process')}
                    </div>

                    <Link to="/sales/orders">
                        <button className="dark">
                            {t('order_management')}
                        </button>
                    </Link>
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }}>{t('number_short')}</th>
                            <th style={{ width: '10%' }}>{t('source')}</th>
                            <th style={{ width: '40%' }}>{t('distributor')}</th>
                            <th style={{ minWidth: '110px' }}>{t('created_at')}</th>
                            <th style={{ minWidth: '110px' }}>{t('amount')}</th>
                            <th style={{ width: '20%' }}>{t('status')}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data?.orders?.map((item, index) => (
                            <tr key={index}>
                                <td className="text-second">{item.order_number}</td>
                                <td>
                                    <div className={`source source-${item.source}`}>
                                        {item.source}
                                    </div>
                                </td>
                                <td>{item.distributor_name}</td>
                                <td>{formatDate(item.created_at)}</td>
                                <td>{formatMoney(item.total_amount)} {t('uzs')}</td>
                                <td>
                                    <div className={`alert alert-${item.status} w-fit-content`}>
                                        {t(item.status)}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    paginate={handlePaginate}
                    pagination={{
                        total: data?.count || 0
                    }}
                />
            </div>

            <div className="card">
                <div className="d-between mb-30">
                    <div className="fz16 text-third">
                        {t('product_availability')}
                    </div>

                    <Link to="/sales/warehouse-fg">
                        <button className="white">
                            {t('more_details')}
                        </button>
                    </Link>
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }}>{t('sku')}</th>
                            <th style={{ width: '40%' }}>{t('name')}</th>
                            <th style={{ width: '10%' }}>{t('available')}</th>
                            <th style={{ width: '20%' }}>{t('reserved')}</th>
                            <th style={{ width: '20%' }}>{t('status')}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data?.availability?.map((item, index) => (
                            <tr key={index}>
                                <td className="text-second">{item.sku}</td>
                                <td>{item.name}</td>
                                <td>{formatMoney(item.available)} {item.unit}</td>
                                <td className="text-warning">{formatMoney(item.reserved)} {item.unit}</td>
                                <td>
                                    <div className={`alert alert-${item.status} w-fit-content`}>
                                        {t(item.status)}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    paginate={handlePaginate}
                    pagination={{
                        total: data?.count || 0
                    }}
                />
            </div>

            <div className="card">
                <div className="mb-30 fz16 text-third">
                    {t('quick_actions')}
                </div>

                <div className="d-flex gap-16 w-100">
                    <button className="dark second" onClick={() => setAcceptanceProductionModal(true)}>
                        <OrdersIcon />
                        {t('create_order')}
                    </button>

                    <button className="white second" onClick={() => setShipmentDistributorModal(true)}>
                        <ClockIcon />
                        {t('process_orders')}
                    </button>

                    <Link to="/sales/warehouse-fg" className="w-100">
                        <button className="white second">
                            <ReportUpIcon />
                            {t('check_stock')}
                        </button>
                    </Link>
                </div>
            </div>

            <AcceptanceProduction modal={acceptanceProductionModal} setModal={setAcceptanceProductionModal} />
            <ShipmentDistributor modal={shipmentDistributorModal} setModal={setShipmentDistributorModal} />
        </>
    )
}

export default Dashboard